import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import SetupStepperSidebar from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/setup-stepper-sidebar';
import StepFeedProgram from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-feed-program';
import StepFinance from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-finance';
import StepOverview from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-overview';
import StepReviewAndRun from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-review-and-run';
import { ScenarioSetupStepProps } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-setup-step-props.type';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';

// On click will be called in the onSuccess after posting the data for each step
const RenderStep: React.FC<ScenarioSetupStepProps> = ({ onClick, activeStep, isFirstStep, isLastStep, canProceed }) => {
  switch (activeStep) {
    case 0:
      return (
        <StepOverview
          onClick={onClick}
          activeStep={activeStep}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          canProceed={canProceed}
        />
      );
    case 1:
      return (
        <StepFeedProgram
          onClick={onClick}
          activeStep={activeStep}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          canProceed={canProceed}
        />
      );
    case 2:
      return (
        <StepFinance
          onClick={onClick}
          activeStep={activeStep}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          canProceed={canProceed}
        />
      );
    case 3:
      return (
        <StepReviewAndRun
          onClick={onClick}
          activeStep={activeStep}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          canProceed={canProceed}
        />
      );
    default:
      return null;
  }
};

const lockedStates = [ScenarioState.SUBMITTED, ScenarioState.COMPLETED, ScenarioState.SCENARIO_RESULT_SENT];

const MAX_STEPS = 4;

const ScenarioSetupTab = () => {
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (lockedStates.includes(scenario?.state as ScenarioState)) {
      setActiveStep(MAX_STEPS - 1);
    }
  }, [scenario?.state]);

  /**
   #TODO Step one scenario state validations:
   - In the current scenario state, all cages with `isIncluded` true must have a startDate before the scenario end date.
 *
 */
  const validateStepOne = () => true;

  /**
   * Step two validations
   */
  const validateStepTwo = () => true;

  /**
   * Step three validations
   */
  const validateStepThree = () => true;

  /**
   * Step four validations
   */
  const validateStepFour = () => true;

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return validateStepOne();
      case 1:
        return validateStepTwo();
      case 2:
        return validateStepThree();
      case 3:
        return validateStepFour();
      default:
        return true;
    }
  };

  const handleStep = (step: number) => {
    const isLocked = lockedStates.includes(scenario?.state as ScenarioState);
    if (isLocked && step < MAX_STEPS - 1) return;
    const clampedStep = Math.max(0, Math.min(step, MAX_STEPS - 1));
    setActiveStep(clampedStep);
  };

  return (
    <Box>
      <Box display={'flex'} width={'100%'} gap={4} alignItems={'flex-start'}>
        <Box flex={'0 0 auto'} display={'flex'} paddingX={5} alignSelf={'flex-start'}>
          <SetupStepperSidebar activeStep={activeStep} />
        </Box>
        <Box flex="1 1 auto" display={'flex'} flexDirection={'column'} alignSelf={'stretch'}>
          <Box flex={'1 1 auto'}>
            <RenderStep
              activeStep={activeStep}
              onClick={handleStep}
              isFirstStep={activeStep === 0}
              isLastStep={activeStep === MAX_STEPS - 1}
              canProceed={canProceed()}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ScenarioSetupTab;
